import { DataHeader, DataSourceMode, DropdownSelectionEvent, TableRow, TableRowDisplayEvent, TableRowMode, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutMobileCommExAppControlTable } from "./autogen/AutogenLayoutMobileCommExAppControlTable";
import { MobileCommExAppControl } from "./MobileCommExAppControl";

// Constants for group by values
const GROUP_BY_VENDOR = "V";
const GROUP_BY_FORM = "F";
const GROUP_BY_TITLE = "T";

export class MobileCommExAppControlTable extends AutogenLayoutMobileCommExAppControlTable {

    async onLoad(): Promise<void> {
        if (this.textboxGroupBy.items.length > 0) {
            this.textboxGroupBy.setSelectedItemFromValue(this.textboxGroupBy.items[1].value);
        }

        this.tableGroupByVendor.overrideUrlParams = (selectedRow: TableRow) => {
            return {
                key: selectedRow.data.get("id"),
                formType: "I",
                mcControlId: selectedRow.data.get("mc_control_id", selectedRow.data.get("mc_control_id")),
            };
        }
    }

    textboxGroupByAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const groupByValue = event.selection.value;

        if (groupByValue === GROUP_BY_VENDOR) {
            this.handleVendorGrouping();
        } else if (groupByValue === GROUP_BY_FORM) {
            this.handleFormGrouping();
        } else if (groupByValue === GROUP_BY_TITLE) {
            this.handleTitleGrouping();
        }
    }

    /**
     * Configures the UI for vendor-based grouping.
     */
    private async handleVendorGrouping(): Promise<void> {
        await this.tableGroupByVendor.resetColumnSorting()
        this.tableGroupByVendor.applySort("mc_control_id", "asc");
    }

    /**
     * Configures the UI for form-based grouping.
     */
    private async handleFormGrouping(): Promise<void> {
        await this.tableGroupByVendor.resetColumnSorting()
        this.tableGroupByVendor.applySort("id", "asc");
    }

    /**
     * Configures the UI for form-based grouping.
     */
    private async handleTitleGrouping(): Promise<void> {
        await this.tableGroupByVendor.resetColumnSorting()
        this.tableGroupByVendor.applySort("mc_form.title", "asc");
    }

    tableOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.mode === TableRowMode.QUICK_ADD) {
            tableRow.visible = false;
        }
    }

    async tableOnSelect(event: TableSelectionEvent): Promise<void> {

        const exAppLayout = this.owner.layoutExAppControl as MobileCommExAppControl;

        if (!event.newRow) {
            exAppLayout.visible = false;
            return;
        }

        const modelRow = event.newRow.data;
        // Save the selected index so we can restore it after data operations
        // that might cause the table to refresh and lose selection state
        const selectedIndex = this.tableGroupByVendor.selectedIndex;

        const formId = modelRow?.get("id", null);
        const vendorId = modelRow?.getString("mc_control_id", null);
        const formType = "I";

        if (formId) {
            await this.setupFormLayout(exAppLayout, formId, vendorId, formType, selectedIndex);
            exAppLayout.visible = true;
        }
    }

    private async setupFormLayout(
        controlLayout: MobileCommExAppControl,
        formId: string,
        vendorId: string,
        formType: string,
        selectedIndex: number
    ): Promise<void> {
        // Load data
        controlLayout.mainDataSource.mode = DataSourceMode.UPDATE;
        await controlLayout.mainDataSource.search({ id: formId, mc_control_id: vendorId, form_type: formType });

        // Restore the table selection after the search (which may have caused a refresh)
        this.tableGroupByVendor.selectedIndex = selectedIndex;
        this.tableGroupByVendor.selectedRow?.scrollIntoView();

        // Configure data header
        this.configureDataHeader(controlLayout, selectedIndex);
    }

    private configureDataHeader(controlLayout: MobileCommExAppControl, selectedIndex: number): void {
        // Set up navigation and data source
        controlLayout.dataHeader.navigateOnClose = async () => {
            await this.mainDataSource.search(this.mainDataSource.lastSearch);
            controlLayout.visible = false;
        };

        controlLayout.dataHeader.borderBottomWidth = 1;
        const saveButton = controlLayout.dataHeader?.["saveButton"];
        if (saveButton) {
            saveButton.dataSource = controlLayout.mainDataSource;
        }
        controlLayout.dataHeader.dataSource = controlLayout.mainDataSource;

        // Remove existing data header if present (search all components, not just first)
        const existingHeaderIndex = controlLayout.components.findIndex(c => c instanceof DataHeader);
        if (existingHeaderIndex !== -1) {
            controlLayout.components.splice(existingHeaderIndex, 1);
        }

        // Set up execute handler
        controlLayout.dataHeader.onExecute = async () => {
            controlLayout.mainDataSource.post();
            await this.mainDataSource.search(this.mainDataSource.lastSearch);
            // Restore selection after the table refreshes from the search
            this.tableGroupByVendor.selectedIndex = selectedIndex;
        };

        // Add data header to the beginning
        controlLayout.components.unshift(controlLayout.dataHeader);
        controlLayout.relayout();
    }
}
